import {
  Autocomplete,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  FiCheckCircle,
  FiEdit,
  FiList,
  FiRotateCcw,
  FiTrash2,
  FiXCircle
} from "react-icons/fi";
import { Link } from "react-router-dom";
import {
  CompanyInterface,
  FinancialCategoryInterface,
  FinancialEntryInterface
} from "../../common/interfaces";
import { extractErrorMessage } from "../../common/utils/axios.isAxiosError";
import ConfirmDialog from "../../components/ConfirmDialog";
import CreateButton from "../../components/CreateButton";
import CustomTooltip from "../../components/CustomTooltip";
import InfoBanner from "../../components/InfoBanner";
import {
  FinancialEntryStatus,
  FinancialEntryStatusLabel,
  FinancialEntryType,
  FinancialEntryTypeLabel,
  FinancialPaymentMethodLabel
} from "../../components/enum/financialEnums";
import FinancialEntryCancelModal from "../../components/modal/FinancialEntryCancelModal";
import FinancialEntryCreateModal from "../../components/modal/FinancialEntryCreateModal";
import FinancialEntrySettleModal from "../../components/modal/FinancialEntrySettleModal";
import PaginationControls from "../../components/PaginationControls";
import { ToastError, ToastSuccess } from "../../components/toaster/toast.utils";
import { useAuth } from "../../hooks/useAuth";
import api from "../../services/api";
import {
  ActionButton,
  AmountCell,
  Container,
  FilterCard,
  StatusCell,
  Table,
  TableContainer,
  TabsBar,
  TabsRow,
  TextFieldSearch,
  Title
} from "./financial.styled";

const brl = (n: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    n || 0
  );

const fmtDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString("pt-BR") : "-";

const FinancialEntries: React.FC = () => {
  const { isAdmin } = useAuth();
  const [entries, setEntries] = useState<FinancialEntryInterface[]>([]);
  const [categories, setCategories] = useState<FinancialCategoryInterface[]>(
    []
  );
  const [companies, setCompanies] = useState<CompanyInterface[]>([]);
  const [selectedCompany, setSelectedCompany] =
    useState<CompanyInterface | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [search, setSearch] = useState("");
  const [type, setType] = useState<FinancialEntryType | "">("");
  const [status, setStatus] = useState<FinancialEntryStatus | "">("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [onlyMine, setOnlyMine] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [settleOpen, setSettleOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] =
    useState<FinancialEntryInterface | null>(null);

  // ADMIN sem empresa escolhida não tem categoria pra listar.
  const waitingCompany = isAdmin && !selectedCompany;

  const fetchCategories = async () => {
    if (waitingCompany) {
      setCategories([]);
      return;
    }
    try {
      const { data } = await api.get("/financial/categories", {
        params: {
          limit: 100,
          company_id: isAdmin ? selectedCompany?.company_id : undefined
        }
      });
      setCategories(data.items ?? []);
    } catch (error) {
      ToastError(extractErrorMessage(error));
    }
  };

  const fetchEntries = async (currentPage: number) => {
    try {
      const { data } = await api.get("/financial/entries", {
        params: {
          page: currentPage,
          limit: 10,
          search: search.trim() || undefined,
          type: type || undefined,
          status: status || undefined,
          initial_date: startDate || undefined,
          final_date: endDate || undefined,
          only_mine: onlyMine || undefined,
          company_id: isAdmin ? selectedCompany?.company_id : undefined
        }
      });
      setEntries(data.items ?? []);
      setTotalPages(data.meta?.totalPages ?? 1);
    } catch (error) {
      ToastError(extractErrorMessage(error));
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    api
      .get("/company/admin")
      .then((r) => setCompanies(r.data.items ?? r.data ?? []))
      .catch((error) => ToastError(extractErrorMessage(error)));
  }, [isAdmin]);

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany, waitingCompany]);

  useEffect(() => {
    fetchEntries(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    search,
    type,
    status,
    startDate,
    endDate,
    onlyMine,
    selectedCompany
  ]);

  const handleOpenCreate = () => {
    setSelectedEntry(null);
    setCreateOpen(true);
  };

  const handleOpenEdit = (entry: FinancialEntryInterface) => {
    setSelectedEntry(entry);
    setCreateOpen(true);
  };

  const handleOpenSettle = (entry: FinancialEntryInterface) => {
    setSelectedEntry(entry);
    setSettleOpen(true);
  };

  const handleOpenCancel = (entry: FinancialEntryInterface) => {
    setSelectedEntry(entry);
    setCancelOpen(true);
  };

  const handleOpenDelete = (entry: FinancialEntryInterface) => {
    setSelectedEntry(entry);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedEntry) return;
    try {
      await api.delete(
        `/financial/entries/${selectedEntry.financial_entry_id}`
      );
      ToastSuccess("Lançamento excluído.");
      fetchEntries(page);
    } catch (error) {
      ToastError(extractErrorMessage(error));
    } finally {
      setDeleteOpen(false);
    }
  };

  const handleReopen = async (entry: FinancialEntryInterface) => {
    try {
      await api.post(`/financial/entries/${entry.financial_entry_id}/reopen`);
      ToastSuccess("Lançamento reaberto — voltou para pendente.");
      fetchEntries(page);
    } catch (error) {
      ToastError(extractErrorMessage(error));
    }
  };

  return (
    <Container>
      <Title>Financeiro</Title>

      <TabsBar>
        <TabsRow>
          <Link to="/financial">Visão geral</Link>
          <Link to="/financial/entries" className="active">
            Lançamentos
          </Link>
          <Link to="/financial/categories">Categorias</Link>
        </TabsRow>

        <CustomTooltip title={waitingCompany ? "Escolha uma empresa primeiro" : ""}>
          {/* span porque botão desabilitado não dispara os eventos do tooltip */}
          <span>
            <CreateButton onClick={handleOpenCreate} disabled={waitingCompany}>
              <FiList size={18} /> Criar Lançamento
            </CreateButton>
          </span>
        </CustomTooltip>
      </TabsBar>

      <InfoBanner icon={<FiList size={26} />} title="Lançamentos">
        Cada entrada e saída de dinheiro da empresa, uma por uma. Cadastre,
        edite, quite ou cancele um lançamento aqui — o resumo consolidado
        fica na aba Visão geral.
      </InfoBanner>

      <FilterCard>
        <TextFieldSearch
          label="Buscar por descrição"
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />

        <FormControl size="small" style={{ minWidth: 140 }}>
          <InputLabel>Tipo</InputLabel>
          <Select
            label="Tipo"
            value={type}
            onChange={(e) => setType(e.target.value as FinancialEntryType)}
          >
            <MenuItem value="">Todos</MenuItem>
            {Object.values(FinancialEntryType).map((t) => (
              <MenuItem key={t} value={t}>
                {FinancialEntryTypeLabel[t]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" style={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value as FinancialEntryStatus)}
          >
            <MenuItem value="">Todos</MenuItem>
            {Object.values(FinancialEntryStatus).map((s) => (
              <MenuItem key={s} value={s}>
                {FinancialEntryStatusLabel[s]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="De"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          label="Até"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        {isAdmin && (
          <Autocomplete
            size="small"
            options={companies}
            sx={{ minWidth: 220 }}
            getOptionLabel={(opt) => opt.company_name || ""}
            isOptionEqualToValue={(option, value) =>
              option?.company_id === value?.company_id
            }
            value={selectedCompany}
            onChange={(_, v) => setSelectedCompany(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Empresa"
                // Não é mais "todas se vazio": sem empresa escolhida a lista
                // vem vazia, para não misturar lançamentos de empresas
                // diferentes numa tela só.
                // helperText={
                //   selectedCompany ? undefined : "Escolha uma empresa para ver"
                // }
              />
            )}
          />
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={onlyMine}
              onChange={(e) => {
                setOnlyMine(e.target.checked);
                setPage(1);
              }}
            />
          }
          label="Somente meus lançamentos"
        />
      </FilterCard>

      <TableContainer>
        <Table>
          <thead>
            <tr>
              <th>Descrição</th>
              <th>Categoria</th>
              <th>Competência</th>
              <th>Vencimento</th>
              <th>Valor</th>
              <th>Status</th>
              <th>Forma pgto.</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.financial_entry_id}>
                <td>{entry.description}</td>
                <td>{entry.category?.name ?? "-"}</td>
                <td>{fmtDate(entry.accrual_date)}</td>
                <td>{fmtDate(entry.due_date)}</td>
                <AmountCell type={entry.type}>
                  {entry.type === "EXPENSE" ? "- " : "+ "}
                  {brl(entry.amount)}
                </AmountCell>
                <StatusCell status={entry.status as FinancialEntryStatus}>
                  {
                    FinancialEntryStatusLabel[
                      entry.status as FinancialEntryStatus
                    ]
                  }
                </StatusCell>
                <td>
                  {entry.payment_method
                    ? (FinancialPaymentMethodLabel[
                        entry.payment_method as keyof typeof FinancialPaymentMethodLabel
                      ] ?? entry.payment_method)
                    : "-"}
                </td>
                <td>
                  {(entry.status === "PENDING" ||
                    entry.status === "OVERDUE") && (
                    <>
                      <CustomTooltip title="Editar">
                        <ActionButton onClick={() => handleOpenEdit(entry)}>
                          <FiEdit size={18} />
                        </ActionButton>
                      </CustomTooltip>
                      <CustomTooltip title="Liquidar">
                        <ActionButton onClick={() => handleOpenSettle(entry)}>
                          <FiCheckCircle size={18} color="#34d399" />
                        </ActionButton>
                      </CustomTooltip>
                      <CustomTooltip title="Cancelar">
                        <ActionButton onClick={() => handleOpenCancel(entry)}>
                          <FiXCircle size={18} color="#fb7185" />
                        </ActionButton>
                      </CustomTooltip>
                      {entry.source_type === "MANUAL" && (
                        <CustomTooltip title="Excluir">
                          <ActionButton onClick={() => handleOpenDelete(entry)}>
                            <FiTrash2 size={18} color="#fb7185" />
                          </ActionButton>
                        </CustomTooltip>
                      )}
                    </>
                  )}
                  {entry.status === "SETTLED" &&
                    entry.source_type === "MANUAL" && (
                      <>
                        <CustomTooltip title="Cancelar">
                          <ActionButton onClick={() => handleOpenCancel(entry)}>
                            <FiXCircle size={18} color="#fb7185" />
                          </ActionButton>
                        </CustomTooltip>
                        <CustomTooltip title="Reabrir (voltar para pendente)">
                          <ActionButton onClick={() => handleReopen(entry)}>
                            <FiRotateCcw size={18} color="#fbbf24" />
                          </ActionButton>
                        </CustomTooltip>
                      </>
                    )}
                  {entry.status === "CANCELED" &&
                    entry.source_type === "MANUAL" && (
                      <CustomTooltip title="Excluir">
                        <ActionButton onClick={() => handleOpenDelete(entry)}>
                          <FiTrash2 size={18} color="#fb7185" />
                        </ActionButton>
                      </CustomTooltip>
                    )}
                </td>
              </tr>
            ))}
            {entries.length === 0 && (
              <tr>
                <td
                  colSpan={8}
                  style={{ textAlign: "center", color: "var(--text-dim)" }}
                >
                  Nenhum lançamento encontrado.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </TableContainer>

      <PaginationControls
        page={page}
        totalPages={totalPages}
        setPage={setPage}
      />

      <FinancialEntryCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={() => fetchEntries(page)}
        categories={categories}
        entry={selectedEntry}
        companyId={isAdmin ? selectedCompany?.company_id : undefined}
      />
      <FinancialEntrySettleModal
        open={settleOpen}
        onClose={() => setSettleOpen(false)}
        onSettled={() => fetchEntries(page)}
        entry={selectedEntry}
      />
      <FinancialEntryCancelModal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onCanceled={() => fetchEntries(page)}
        entry={selectedEntry}
      />
      <ConfirmDialog
        open={deleteOpen}
        title="Excluir lançamento"
        description={`Excluir "${selectedEntry?.description ?? ""}" definitivamente? Essa ação não pode ser desfeita.`}
        confirmText="Excluir"
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </Container>
  );
};

export default FinancialEntries;
